import getFetchHeaders from '../../../utils/header.utils';
import {backendUrl} from '../../constants/endpoints';
import {ApiErrorHandlingOptions} from './types/api-error-handling-options.interface';
import {
  BaseRequestPayload,
  FetchResponse,
  PaginatedFetchResponse,
  PaginatedRequestPayload,
  QueryParams,
} from './types/api-request.types';

const NO_CONTENT_STATUS = new Set([204, 205]);

export const buildQueryString = (queryParams?: QueryParams): string => {
  if (!queryParams) {
    return '';
  }

  const normalized = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    normalized.set(key, String(value));
  });

  const serialized = normalized.toString();
  return serialized ? `?${serialized}` : '';
};

async function baseApiRequest<
  TResponse,
  TRequest,
  TErrorReturn =
    | FetchResponse<TResponse>
    | PaginatedFetchResponse<TResponse>
    | null,
>(
  request: BaseRequestPayload<TRequest>,
  excludeHeaders: boolean,
  accessToken: string | null,
  {
    errorMessage = 'API request failed',
    defaultValue,
    onError,
    shouldPrintLogs = true,
  }: ApiErrorHandlingOptions<TErrorReturn> = {},
): Promise<
  | FetchResponse<TResponse>
  | PaginatedFetchResponse<TResponse>
  | TErrorReturn
  | null
> {
  try {
    const {method, endpoint, body, queryParams} = request;

    const queryString = buildQueryString(queryParams);
    const url = `${backendUrl}${endpoint}${queryString}`;

    const headers = await getFetchHeaders(excludeHeaders, accessToken);
    const options: RequestInit = {
      headers: {...headers},
      method,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      // Try to read the body for a structured error message before throwing
      let errorBody: any = null;
      try {
        errorBody = await response.json();
      } catch {
        // body was not JSON – fall through
      }
      if (errorBody?.error) {
        throw new Error(`Server error: ${JSON.stringify(errorBody.error)}`);
      }
      // Unhandled server errors come back PascalCased ({Success, Error}).
      if (errorBody?.Error) {
        throw new Error(
          `Server error: ${JSON.stringify({
            code: errorBody.Error.Code,
            message: errorBody.Error.Message,
          })}`,
        );
      }
      throw new Error(`HTTP error status: ${response.status}`);
    }

    if (NO_CONTENT_STATUS.has(response.status)) {
      return null;
    }

    const responseBody = await response.json();

    if (responseBody?.error) {
      throw new Error(`Server error: ${JSON.stringify(responseBody.error)}`);
    }

    return responseBody as
      | FetchResponse<TResponse>
      | PaginatedFetchResponse<TResponse>;
  } catch (error) {
    if (shouldPrintLogs) {
      console.error(errorMessage, error);
    }
    if (onError) {
      onError(error);
    }
    return (defaultValue as TErrorReturn) ?? null;
  }
}

export async function apiRequest<
  TResponse,
  TRequest = undefined,
  TErrorReturn extends FetchResponse<TResponse> | null = null,
>(
  request: BaseRequestPayload<TRequest>,
  errorHandlingOptions?: ApiErrorHandlingOptions<TErrorReturn>,
  excludeHeaders = false,
  accessToken: string | null = null,
): Promise<FetchResponse<TResponse> | TErrorReturn> {
  const response = await baseApiRequest<TResponse, TRequest, TErrorReturn>(
    request,
    excludeHeaders,
    accessToken,
    errorHandlingOptions,
  );

  return response as FetchResponse<TResponse> | TErrorReturn;
}

export async function paginatedApiRequest<
  TResponse,
  TRequest = undefined,
  TErrorReturn extends PaginatedFetchResponse<TResponse> | null = null,
>(
  request: PaginatedRequestPayload<TRequest>,
  errorHandlingOptions?: ApiErrorHandlingOptions<TErrorReturn>,
): Promise<PaginatedFetchResponse<TResponse> | TErrorReturn | null> {
  const response = await baseApiRequest<TResponse, TRequest, TErrorReturn>(
    request,
    false,
    null,
    errorHandlingOptions,
  );

  // An empty page (totalItems 0, maxPage 0) is still a paginated answer.
  if (
    response &&
    typeof response === 'object' &&
    Array.isArray((response as any).data) &&
    typeof (response as any).totalItems === 'number' &&
    typeof (response as any).maxPage === 'number'
  ) {
    return response as PaginatedFetchResponse<TResponse>;
  }

  if (response === null) {
    return errorHandlingOptions?.defaultValue ?? null;
  }

  console.error('Response is not paginated for request:', request);
  return errorHandlingOptions?.defaultValue ?? null;
}

export type {
  BaseRequestPayload,
  FetchResponse,
  PaginatedFetchResponse,
  PaginatedRequestPayload,
  QueryParams,
} from './types/api-request.types';
export type {ApiErrorHandlingOptions} from './types/api-error-handling-options.interface';
